import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/cn";

// Size=xs: 24px pill with Body XS Medium (12px). Height is also pinned via
// inline style below because variant classes set their own h-*; the
// duplication wins the override.
const XS_FIXED_HEIGHT_CLASSES = "!h-[24px] !min-h-[24px] !max-h-[24px]";
const XS_PILL_SIZE_CLASSES = [
  XS_FIXED_HEIGHT_CLASSES,
  "!px-2.5 !py-0.5 !pt-0.5 !pb-0.5 gap-1",
  "!type-body-xs-medium rounded-full box-border",
].join(" ");

const PILL_VARIANTS = [
  "default",
  "destructive",
  "secondary",
  "subtle",
  "gradient",
] as const;

const FIXED_HEIGHT_CLASSES =
  "h-[32px] min-h-[32px] max-h-[32px] [&]:h-[32px] [&]:min-h-[32px] [&]:max-h-[32px]";
const FIXED_SIZE_CLASSES = "px-3 py-0 pt-0 pb-0 box-border";

const FIXED_TYPOGRAPHY = "type-body-s-regular";

const FOCUS_STYLES =
  "focus-visible:border-[var(--focus-border)] focus-visible:ring-0";

const DISABLED_LIGHT =
  "disabled:bg-[#f5f5f5] disabled:text-[#a6a6a6] disabled:border disabled:!border-[var(--color-border)]";

const DISABLED_DARK =
  "dark:disabled:bg-[#313435] dark:disabled:text-[#868686] dark:disabled:border dark:disabled:!border-[var(--color-border)]";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center",
    "whitespace-nowrap rounded-md type-body-s-regular",
    "duration-0 delay-0 transition-colors",
    "ring-offset-white dark:ring-offset-neutral-950",
    "focus-visible:outline-hidden",
    "disabled:pointer-events-none disabled:opacity-50",
    "cursor-pointer",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "bg-[#313435] text-white hover:bg-[#161718]",
          "dark:bg-[#f5f5f5] dark:text-black dark:hover:bg-[#ffffff] dark:hover:text-black",
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          FIXED_TYPOGRAPHY,
          "border-2 border-transparent dark:hover:border-transparent",
          FOCUS_STYLES,
          DISABLED_LIGHT,
          DISABLED_DARK,
          "disabled:opacity-100",
        ].join(" "),
        destructive: [
          "bg-red-500 text-neutral-50 hover:bg-red-600",
          "dark:bg-red-900 dark:text-neutral-50 dark:hover:bg-red-800",
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          "border-2 border-transparent",
          FOCUS_STYLES,
        ].join(" "),
        secondary: [
          "bg-transparent text-[#202122] hover:bg-[#e8e8e8] hover:opacity-100",
          "dark:bg-transparent dark:text-white dark:hover:bg-[#313435] dark:hover:text-white dark:hover:opacity-100",
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          "border border-[var(--color-border)] hover:border-[var(--color-border)]",
          "focus-visible:border-2",
          FOCUS_STYLES,
          "disabled:bg-transparent disabled:text-[#a6a6a6] disabled:border disabled:!border-[var(--color-border)] disabled:opacity-100",
          "dark:disabled:bg-transparent dark:disabled:text-[#868686] dark:disabled:border dark:disabled:!border-[var(--color-border)]",
        ].join(" "),
        subtle: [
          "text-[#202122] hover:text-[#202122]",
          "dark:text-white",
          "border border-transparent hover:border-[var(--color-border)]",
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          "focus-visible:!border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0",
          "disabled:bg-transparent disabled:text-[#a6a6a6] dark:disabled:text-[#868686] disabled:opacity-100",
        ].join(" "),
        "header-icon": [
          "shrink-0 rounded-lg bg-transparent p-0",
          "text-neutral-600 dark:text-neutral-400",
          "hover:bg-black/[0.04] dark:hover:bg-white/[0.04]",
          "focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]",
        ].join(" "),
        inline: [
          "text-inherit hover:text-[#202122]",
          "dark:hover:text-white",
          "bg-transparent border-2 border-transparent rounded",
          "focus-visible:border-[var(--focus-border)] focus-visible:ring-0",
          "disabled:text-[#a6a6a6] dark:disabled:text-[#868686] disabled:opacity-100",
        ].join(" "),
        gradient: [
          "relative bg-gradient-to-r from-[#cceeff] to-[#f3fc50]",
          "hover:from-[#aee3fd] hover:to-[#f0fb29]",
          "dark:from-[#cceeff] dark:to-[#f3fc50] dark:hover:from-[#e3f6ff] dark:hover:to-[#fafeb4]",
          "text-[#202122]",
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          "border border-[var(--color-border)]",
          "focus-visible:border-2",
          FOCUS_STYLES,
          "focus-visible:after:absolute focus-visible:after:inset-0 focus-visible:after:bg-white/20 focus-visible:after:rounded-full",
          "disabled:from-[#e9f7fe] disabled:to-[#fcfed7] dark:disabled:from-[#24495b] dark:disabled:to-[#36380b]",
          "disabled:text-[#a6a6a6] dark:disabled:text-[#868686]",
          "disabled:border disabled:border-[var(--color-border)] disabled:opacity-100",
          "[&>*]:relative [&>*]:z-10",
        ].join(" "),
      },
      size: {
        default: "",
        sm: "", // Backward compatibility — same as default
        xs: "",
        lg: "", // Backward compatibility — same as default
        icon: "", // Backward compatibility — same as default
      },
    },
    compoundVariants: [
      ...PILL_VARIANTS.map((variant) => ({
        variant,
        size: "xs" as const,
        class: XS_PILL_SIZE_CLASSES,
      })),
      {
        variant: "default",
        size: "xs",
        class: "enabled:!border-0",
      },
      {
        variant: "secondary",
        size: "xs",
        class: [
          "!border !border-[var(--color-border)]",
          "hover:!border-[var(--color-border)]",
          "text-[#1d1e20] dark:text-white",
        ].join(" "),
      },
      {
        variant: "subtle",
        size: "xs",
        class:
          "!border !border-transparent hover:!border-[var(--color-border)]",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Any icon component taking `className` and `size` (Lucide, simple-icons, …). */
  icon?: React.ComponentType<{ className?: string; size?: number | string }>;
  iconPosition?: "left" | "right";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      icon: Icon,
      iconPosition = "left",
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isXs = size === "xs";
    const isPillVariant =
      variant != null && (PILL_VARIANTS as readonly string[]).includes(variant);
    const isXsPill = isXs && isPillVariant;
    const needsDefaultFixedHeight = !isXs && isPillVariant;

    const iconSize = isXs ? 12 : 16;
    const hasIcon = !!Icon;

    const isTextOnly =
      !hasIcon &&
      (typeof children === "string" || typeof children === "number");

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          !isXs && hasIcon && iconPosition === "left" && "!pl-2.5 !pr-3",
          !isXs && hasIcon && iconPosition === "right" && "!pl-3 !pr-2.5",
          // The icon reads lighter than the label, so its side loses 2px.
          isXs && hasIcon && iconPosition === "left" && "!pl-2 !pr-2.5",
          isXs && hasIcon && iconPosition === "right" && "!pl-2.5 !pr-2",
        )}
        style={
          isXsPill
            ? {
                height: "24px",
                minHeight: "24px",
                maxHeight: "24px",
                ...style,
              }
            : needsDefaultFixedHeight
              ? {
                  height: "32px",
                  minHeight: "32px",
                  maxHeight: "32px",
                  ...style,
                }
              : style
        }
        ref={ref}
        {...props}
      >
        {Icon && iconPosition === "left" && (
          <Icon
            className={cn("shrink-0", isXs ? "" : "mr-1")}
            size={iconSize}
          />
        )}
        {isTextOnly ? (
          <span
            className={cn(
              "inline-flex items-center",
              isXs ? "leading-[16px]" : "leading-[1]",
            )}
          >
            {children}
          </span>
        ) : (
          <Slottable>{children}</Slottable>
        )}
        {Icon && iconPosition === "right" && (
          <Icon
            className={cn("shrink-0", isXs ? "" : "ml-1")}
            size={iconSize}
          />
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
